import { parseRequiredHashtags } from '@/lib/parseRequiredHashtags';
import { platformCpmRateCents, type CpmRateCampaign } from './cpmOverrideOptions';

export type CpmRatePlatform = 'youtube' | 'instagram' | 'tiktok';

export type CampaignCpmRates =
  | { kind: 'single'; rateCents: number }
  | {
    kind: 'per_platform';
    rates: { platform: CpmRatePlatform; rateCents: number }[];
    maxRateCents: number;
  };

export type CampaignCpmRatesInput = CpmRateCampaign & {
  allowed_platforms?: readonly string[] | null;
  /** When set, the rates shown follow the networks the campaign advertises. */
  required_hashtags?: string | null;
};

const CPM_PLATFORMS: readonly CpmRatePlatform[] = ['youtube', 'instagram', 'tiktok'];
// Display order among equal rates.
const TIE_ORDER: readonly CpmRatePlatform[] = ['instagram', 'tiktok', 'youtube'];

/** The CPM networks a campaign accepts: its allowed list, or all three when unrestricted. */
export function considerCpmPlatforms(
  allowedPlatforms: readonly string[] | null | undefined,
): CpmRatePlatform[] {
  if (!allowedPlatforms || allowedPlatforms.length === 0) return [...CPM_PLATFORMS];
  return CPM_PLATFORMS.filter((platform) => allowedPlatforms.includes(platform));
}

/**
 * The networks to list: the accepted ones that the campaign advertises with
 * required hashtags (the "Redes permitidas" chips), plus any accepted network
 * with its own rate. Without per-network hashtags, every accepted network.
 */
function displayedCpmPlatforms(
  campaign: CampaignCpmRatesInput,
  accepted: CpmRatePlatform[],
): CpmRatePlatform[] {
  if (campaign.required_hashtags === undefined) return accepted;
  const hashtags = parseRequiredHashtags(campaign.required_hashtags ?? null);
  const advertised = accepted.filter((platform) => !!hashtags[platform]);
  if (advertised.length === 0) return accepted;
  const rated = new Set((campaign.cpm_platform_rates ?? []).map((rate) => rate.platform));
  return accepted.filter((platform) => advertised.includes(platform) || rated.has(platform));
}

/**
 * How a campaign's CPM should be shown: one rate when every network it
 * accepts pays the same (as before per-network rates existed), otherwise each
 * listed network's rate, highest first. The single/per-network decision looks
 * at every accepted network, so hiding an unadvertised one can never make the
 * campaign read as paying one rate it does not pay everywhere.
 */
export function describeCampaignCpmRates(campaign: CampaignCpmRatesInput): CampaignCpmRates {
  const accepted = considerCpmPlatforms(campaign.allowed_platforms);
  const rateOf = (platform: CpmRatePlatform) => platformCpmRateCents(campaign, platform);
  const distinct = new Set(accepted.map(rateOf));
  if (accepted.length === 0 || distinct.size === 1) {
    return { kind: 'single', rateCents: accepted.length ? rateOf(accepted[0]) : (campaign.cpm_rate_cents || 0) };
  }
  const rates = displayedCpmPlatforms(campaign, accepted)
    .map((platform) => ({ platform, rateCents: rateOf(platform) }))
    .sort((a, b) => (b.rateCents - a.rateCents)
      || (TIE_ORDER.indexOf(a.platform) - TIE_ORDER.indexOf(b.platform)));
  return {
    kind: 'per_platform',
    rates,
    maxRateCents: Math.max(...accepted.map(rateOf)),
  };
}

/** The highest rate a campaign pays, for "Paga até" headlines and sorting. */
export function campaignMaxCpmRateCents(campaign: CampaignCpmRatesInput): number {
  const described = describeCampaignCpmRates(campaign);
  return described.kind === 'single' ? described.rateCents : described.maxRateCents;
}
